import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import "./PublishersPage.css";

export default function PublishersPage({ locale = "kz" }) {
  const t = useMemo(
    () => ({
      kz: {
        title: "Баспаларды басқару",
        subtitle: "Publisher үшін system_code (шифр коды) шығару және тексеру",
        tabs: { staff: "Баспалар тізімі", issue: "Шифр код беру" },
        reload: "Жаңарту",
        searchPh: "Іздеу: BIN / IIN / email / code...",
        tableTitleStaff: "Баспалар тізімі",
        issueMissing: "Шифр коды жоқтарға беру (only_missing)",
        force: "Шифр код жаңарту (force)",
        issueSelected: "Таңдалғандарға беру",
        issueAllMissing: "Барлық жоқтарға беру",
        picked: "Таңдалды",
        total: "Барлығы",
        tip: "Кеңес: алдымен кестеден ұйымдарды таңдаңыз → сосын “Таңдалғандарға беру”.",
        result: "Нәтиже",
        empty: "Дерек жоқ",
        cols: {
          pick: "Таңдау",
          id: "ID",
          bin: "BIN",
          iin: "IIN",
          email: "Email",
          code: "System code",
          has: "Код",
        },
        badges: { has: "Бар", none: "Жоқ" },
      },
      ru: {
        title: "Управление издательствами",
        subtitle: "Генерация и проверка system_code (шифр-код) для Publisher",
        tabs: { staff: "Список издательств", issue: "Выдать шифр-код" },
        reload: "Обновить",
        searchPh: "Поиск: BIN / IIN / email / code...",
        tableTitleStaff: "Список издательств",
        issueMissing: "Выдавать только без кода (only_missing)",
        force: "Перезаписать код (force)",
        issueSelected: "Выдать выбранным",
        issueAllMissing: "Выдать всем без кода",
        picked: "Выбрано",
        total: "Всего",
        tip: "Совет: сначала отметь организации в таблице → затем “Выдать выбранным”.",
        result: "Результат",
        empty: "Нет данных",
        cols: {
          pick: "Выбор",
          id: "ID",
          bin: "BIN",
          iin: "IIN",
          email: "Email",
          code: "System code",
          has: "Код",
        },
        badges: { has: "Есть", none: "Нет" },
      },
    }),
    []
  );

  const L = locale === "kz" ? t.kz : t.ru;

  const [tab, setTab] = useState("staff"); // staff | issue
  const [q, setQ] = useState("");

  const [staffItems, setStaffItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [force, setForce] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pretty = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const clearStatus = () => {
    setError(null);
    setResult(null);
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = (ids) => {
    setSelectedIds((prev) => (prev.length === ids.length ? [] : [...ids]));
  };

  const loadStaff = async () => {
    setLoading(true);
    clearStatus();
    try {
      const res = await axios.get("/backoffice/publishers/encrypted-name");
      setStaffItems(res.data?.items || []);
    } catch (e) {
      setError(e?.response?.data || { message: e?.message || "Request failed" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // если переключили вкладку — просто обновим staff (чтобы статистика не устаревала)
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const issue = async ({ mode }) => {
    // mode: "selected" | "all_missing"
    setLoading(true);
    clearStatus();

    try {
      const body = {
        only_missing: mode === "all_missing" ? true : !!onlyMissing,
        force: !!force,
      };

      if (mode === "selected") body.publisher_ids = selectedIds;

      const res = await axios.post("/backoffice/publishers/encrypted-name/issue", body);
      setResult(res.data);

      await loadStaff();
    } catch (e) {
      setError(e?.response?.data || { message: e?.message || "Request failed" });
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = staffItems.filter((it) => {
    const s = [it?.id, it?.bin, it?.iin, it?.email, it?.system_code]
      .map((x) => String(x ?? "").toLowerCase())
      .join(" ");
    return s.includes(q.trim().toLowerCase());
  });

  const idsInTable = filteredItems.map((x) => x.id);
  const allChecked = idsInTable.length && selectedIds.length === idsInTable.length;

  const totalCount = staffItems.length;
  const hasCount = staffItems.filter((x) => !!x.has_value).length;
  const missingCount = totalCount - hasCount;

  const selectMissingOnPage = () => {
    const missingIds = filteredItems.filter((x) => !x.has_value).map((x) => x.id);
    setSelectedIds(missingIds);
  };

  return (
    <section className="pub-wrap">
      <div className="pub-card">
        {/* Header */}
        <div className="pub-head">
          <div>
            <h2 className="pub-title">{L.title}</h2>
            <p className="pub-sub">{L.subtitle}</p>
          </div>

          <div className="pub-actions">
            <div className="pub-tabs">
              <button className={`pub-tab ${tab === "staff" ? "is-active" : ""}`} onClick={() => setTab("staff")}>
                {L.tabs.staff}
              </button>
              <button className={`pub-tab ${tab === "issue" ? "is-active" : ""}`} onClick={() => setTab("issue")}>
                {L.tabs.issue}
              </button>
            </div>

            <button className="pub-btn pub-btn-ghost" disabled={loading} onClick={loadStaff}>
              {L.reload}
            </button>
          </div>
        </div>

        {/* Top cards */}
        <div className="pub-topgrid">
          <div className="pub-mini">
            <h3>{locale === "kz" ? "1) Тізім" : "1) Список"}</h3>
            <p>
              {locale === "kz"
                ? "BIN/IIN/Email және system_code (бар/жоқ) көрсетіледі."
                : "Показывает BIN/IIN/Email и system_code (есть/нет)."}
            </p>
          </div>

          <div className="pub-mini">
            <h3>{locale === "kz" ? "2) Шифр код беру" : "2) Выдать код"}</h3>
            <p>
              {locale === "kz"
                ? "Коды жоқтарға жаңа system_code береді. Force — барын да қайта жазады."
                : "Выдаёт system_code тем, у кого нет. Force — перезаписывает существующие."}
            </p>
          </div>

          <div className="pub-mini">
            <h3>{locale === "kz" ? "3) Таңдау" : "3) Выбор"}</h3>
            <p>
              {locale === "kz"
                ? "Кестеден белгілеп, тек таңдалғандарға операция жасаңыз."
                : "Отмечай в таблице и выполняй операцию только для выбранных."}
            </p>
          </div>
        </div>

        {/* Issue controls */}
        {tab === "issue" && (
          <div className="pub-form">
            <div className="pub-row">
              <div className="pub-left">
                <label className="pub-switch">
                  <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
                  {L.issueMissing}
                </label>

                <label className="pub-switch">
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                  {L.force}
                </label>

                <div className="pub-stat">
                  <span className="pub-chip ok">
                    {L.picked}: <b>{selectedIds.length}</b>
                  </span>
                  <span className="pub-chip warn">
                    {L.total}: <b>{totalCount}</b>
                  </span>
                  <span className="pub-chip ok">
                    {locale === "kz" ? "Коды бар" : "С кодом"}: <b>{hasCount}</b>
                  </span>
                  <span className="pub-chip bad">
                    {locale === "kz" ? "Коды жоқ" : "Без кода"}: <b>{missingCount}</b>
                  </span>
                </div>
              </div>

              <div className="pub-right">
                <button className="pub-btn pub-btn-ghost" disabled={loading} onClick={selectMissingOnPage}>
                  {locale === "kz" ? "Коды жоқтарды таңдау" : "Выбрать без кода"}
                </button>

                <button
                  className="pub-btn pub-btn-primary"
                  disabled={loading || selectedIds.length === 0}
                  onClick={() => issue({ mode: "selected" })}
                >
                  {L.issueSelected}
                </button>

                <button className="pub-btn pub-btn-warn" disabled={loading} onClick={() => issue({ mode: "all_missing" })}>
                  {L.issueAllMissing}
                </button>
              </div>
            </div>

            <p className="pub-hint">{L.tip}</p>
          </div>
        )}

        {/* Table */}
        <div className="pub-tablewrap">
          <div className="pub-tabletop">
            <div className="pub-tabletitle">{L.tableTitleStaff}</div>

            <div className="pub-search">
              <input className="pub-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.searchPh} />
              <span className="pub-chip warn">
                {L.total}: <b>{totalCount}</b>
              </span>
              <span className="pub-chip ok">
                {locale === "kz" ? "Коды бар" : "С кодом"}: <b>{hasCount}</b>
              </span>
              <span className="pub-chip bad">
                {locale === "kz" ? "Коды жоқ" : "Без кода"}: <b>{missingCount}</b>
              </span>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="pub-table">
              <thead>
                <tr>
                  <th>
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!allChecked}
                        onChange={() => toggleAll(idsInTable)}
                        disabled={!idsInTable.length}
                      />
                      {L.cols.pick}
                    </label>
                  </th>
                  <th>{L.cols.id}</th>
                  <th>{L.cols.bin}</th>
                  <th>{L.cols.iin}</th>
                  <th>{L.cols.email}</th>
                  <th>{L.cols.code}</th>
                  <th>{L.cols.has}</th>
                </tr>
              </thead>

              <tbody>
                {!filteredItems.length ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 14, color: "#64748b" }}>
                      {loading ? "..." : L.empty}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <input type="checkbox" checked={selectedIds.includes(it.id)} onChange={() => toggleOne(it.id)} />
                      </td>
                      <td className="pub-td-mono">{it.id}</td>
                      <td className="pub-td-mono">{it.bin ?? "—"}</td>
                      <td className="pub-td-mono">{it.iin ?? "—"}</td>
                      <td>{it.email ?? "—"}</td>
                      <td className="pub-td-mono">{it.system_code ?? "—"}</td>
                      <td>
                        <span className={`pub-pill ${it.has_value ? "ok" : "empty"}`}>
                          {it.has_value ? L.badges.has : L.badges.none}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Result */}
        <div className="pub-result">
          <div className="pub-result-head">
            <div className="pub-result-title">{L.result}</div>
          </div>

          {error && <pre className="pub-pre is-error">{pretty(error)}</pre>}

          {result && <pre className="pub-pre">{pretty(result)}</pre>}

          {!error && !result && (
            <pre className="pub-pre">
              {locale === "kz" ? "Нәтиже жоқ. Жоғарыдан әрекет жасаңыз." : "Нет результата. Выполните действие сверху."}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
}
